import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ModalService} from '../../../services/modal/modal.service';
import {AppDialogModal} from '../../../models/modal/dialog/app-dialog-modal.model';
import {APP_MODAL_REF} from '../../../constants/modal/app-modal-ref.constant';
import {ImportDialogData} from '../../../models/import/import-dialog-data.model';
import {ImportDialogResult} from '../../../models/import/import-dialog-result.model';
import {AsyncPipe} from '@angular/common';
import {ButtonComponent} from '../../buttons/button/button.component';
import {TranslatePipe} from '@ngx-translate/core';
import {AppButtonTypeEnum} from '../../../enums/button/app-button-type.enum';
import {ButtonColorEnum} from '../../../enums/button/button-color.enum';
import {ICONS} from '../../../constants/icons/icons.constants';
import {BehaviorSubject} from 'rxjs';
import {BasicSubscribableComponent} from '../../dummy-components/basic-subscribable-component';
import {ImportService} from '../../../services/import/import.service';
import {ApiIconUrlPipe} from '../../../pipes/api-icon-url/api-icon-url.pipe';
import {AuthImagePipe} from '../../../pipes/auth-image.pipe';
import {DocumentFilenameComponent} from '../../document-filename/document-filename.component';
import {IconComponent} from '../../icon/icon.component';
import {KebabCasePipe} from '../../../pipes/kebab-case/kebab-case.pipe';
import {AppIconSizeEnum} from '../../../enums/app-icon-size.enum';
import {StatusFile} from '../../../models/status-file/status-file.model';
import {DefaultFileExtensionIconIds} from '../../../api/models/default-file-extension-icon-ids';
import {ImportFormDialogComponent} from './import-form-dialog/import-form-dialog.component';
import {UploadFormData} from '../../../models/import/upload-form-data.model';
import {ImportLocationEnum} from '../../../enums/import-location.enum';
import {Document} from '../../../api/models/document';
import {CheckedOutDocumentService} from '../../../services/checked-out-document/checked-out-document.service';
import {FileUploadStatusEnum} from '../../../enums/file-upload-status.enum';

@Component({
    selector: 'app-import-dialog-modal',
    imports: [
        AsyncPipe,
        ButtonComponent,
        TranslatePipe,
        ApiIconUrlPipe,
        AuthImagePipe,
        DocumentFilenameComponent,
        IconComponent,
        KebabCasePipe,
        ImportFormDialogComponent
    ],
    templateUrl: './import-dialog-modal.component.html',
    styleUrl: './import-dialog-modal.component.css',
})
export class ImportDialogModalComponent extends BasicSubscribableComponent implements OnInit {
    public readonly modalData: ImportDialogData;
    protected formValid: WritableSignal<boolean> = signal(false);
    protected showForm: WritableSignal<boolean> = signal(true);
    protected filesWithStatus: WritableSignal<Array<StatusFile>> = signal([]);
    protected importDone$: Promise<boolean> | undefined;
    protected files$: BehaviorSubject<Array<File>>;
    protected uploadResult: Array<StatusFile> = [];
    protected readonly qaTag: string;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly ButtonColorEnum = ButtonColorEnum;
    protected readonly ICONS = ICONS;
    protected readonly AppIconSize = AppIconSizeEnum;
    protected readonly FileUploadStatusEnum = FileUploadStatusEnum;
    private defaultFileExtensionIconIds: DefaultFileExtensionIconIds;
    private dialogResult: ImportDialogResult;
    private readonly modalRef = inject<AppDialogModal<ImportDialogModalComponent, ImportDialogData, ImportDialogResult>>(APP_MODAL_REF);
    private readonly modalService = inject(ModalService);
    private readonly importService = inject(ImportService);
    private readonly checkedOutDocumentService = inject(CheckedOutDocumentService);

    public constructor() {
        super();
        this.qaTag = this.modalRef.qaTag;
        this.modalData = this.modalRef.modalData;
        this.files$ = this.importService.files$;

        this.defaultFileExtensionIconIds = {
            defaultIconId: '',
            fileExtensionIconIds: []
        };

        this.dialogResult = {
            importLocation: '',
            vaultId: '',
            statusFiles: []
        };
    }

    public async ngOnInit(): Promise<void> {
        this.defaultFileExtensionIconIds = await this.importService.fetchAllFileExtensionIconIds();
    }

    protected close(): void {
        this.importService.resetFiles();

        if (this.filesWithStatus().length === 0) {
            // case for canceling the import dialog
            if (this.modalData.files) {
                for (const file of this.modalData.files) {
                    const status: StatusFile = {
                        file,
                        iconId: '',
                        upload: signal({
                            progress: 0,
                            status: FileUploadStatusEnum.Error,
                            error: {
                                code: 0,
                                message: 'import aborted',
                            },
                            filePath: file.filePath,
                        })
                    };
                    this.dialogResult.statusFiles.push(status);
                }
            }

            this.modalService.closeModal(this.modalRef, this.dialogResult);
            return;
        }

        this.dialogResult.statusFiles = this.uploadResult;

        this.modalService.closeModal(this.modalRef, this.dialogResult);
    }

    protected reset(): void {
        this.importDone$ = undefined;
        if (this.modalData && this.modalData.files) {
            this.modalData.files = [];
        }
        this.filesWithStatus.set([]);
        this.importService.resetFiles();
        this.showForm.set(true);
        this.dialogResult = {
            importLocation: '',
            vaultId: '',
            statusFiles: []
        };
    }

    protected async formDone(formData: UploadFormData): Promise<void> {
        this.showForm.set(false);
        const uploads: Array<Promise<StatusFile>> = [];
        this.dialogResult.importLocation = formData.target;
        this.dialogResult.vaultId = formData.vault.id;

        this.filesWithStatus.set(await Promise.all(this.files$.getValue()
            .map(async file => {
                const fileExtension = '.' + file.name.split('.')
                    .pop();
                const matchedExtensionIconId = this.defaultFileExtensionIconIds.fileExtensionIconIds.find(extensionIconId => extensionIconId.fileExtension === fileExtension);

                const statusFile: StatusFile = {
                    file,
                    iconId: matchedExtensionIconId ? matchedExtensionIconId.iconId : this.defaultFileExtensionIconIds.defaultIconId,
                    upload: signal(undefined)
                };

                if ((Object.values(ImportLocationEnum) as ReadonlyArray<string>).includes(formData.target)) {
                    if (formData.target === ImportLocationEnum.Documents) {
                        uploads.push(this.importService.uploadVaultDocument(statusFile, formData.vault?.id as string, formData.sameFileDetected));
                    } else {
                        if (this.modalData.documentId) {
                            // overwrite the currently active checked out document
                            let activeDocument: Document | undefined;
                            try {
                                activeDocument = await this.checkedOutDocumentService.fetchAndGetCheckedOutDocumentById(this.modalData.documentId);
                            } catch {
                                this.close();
                            }

                            if (activeDocument) {
                                statusFile.upload.set({
                                    status: FileUploadStatusEnum.Waiting,
                                    progress: 0
                                });
                                uploads.push(this.importService.uploadExistingCheckedOutDocument(activeDocument, statusFile));
                            }
                        } else {
                            // In Progress Documents
                            // check if document with name is checked out in the chosen vault
                            const document = await this.checkedOutDocumentService.getCheckedOutDocumentByVaultIdAndName(formData.vault.id, file.name);
                            if (document) {
                                // check if doc with name is already active ( checked out )
                                statusFile.upload.set({
                                    status: FileUploadStatusEnum.Waiting,
                                    progress: 0
                                });
                                uploads.push(this.importService.uploadExistingCheckedOutDocument(document, statusFile));
                            } else {
                                // else upload new file
                                uploads.push(this.importService.uploadNewCheckedOutDocument(statusFile, formData.vault?.id as string, formData.sameFileDetected));
                            }
                        }
                    }
                } else {
                    uploads.push(this.importService.uploadMagnetDocument(statusFile, formData.target, formData.sameFileDetected));
                }
                return statusFile;
            })));

        this.importDone$ =
            Promise.all(uploads)
                .then(data => {
                    this.uploadResult = data;
                    return data.filter(isDone => !isDone).length === 0;
                });
    }
}
